import { idToken, signOut } from "./auth";
import { config } from "./config";
import type * as C from "./contract";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown, auth = false): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (auth) {
    const token = await idToken();
    if (!token) throw new ApiError(401, "unauthorized", "Please sign in again.");
    headers.authorization = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${config.apiUrl}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "network", "Can't reach the salon right now. Check your connection and try again.");
  }

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && auth) signOut();
    throw new ApiError(res.status, data.error ?? "error", data.message ?? "Something went wrong. Please try again.");
  }
  return data as T;
}

const qs = (params: Record<string, string | number | undefined>) =>
  new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== "") as [string, string][]).toString();

export const publicApi = {
  salon: () => request<C.SalonInfoResponse>("GET", "/public/salon"),
  availability: (p: { serviceId: string; stylistId: string; from: string; days: number; exclude?: string }) =>
    request<C.AvailabilityResponse>("GET", `/public/availability?${qs(p)}`),
  startVerification: (phone: string) => request<C.StartVerificationResponse>("POST", "/public/verify", { phone }),
  confirmVerification: (phone: string, code: string) =>
    request<C.ConfirmVerificationResponse>("POST", "/public/verify/confirm", { phone, code }),
  book: (body: C.CreateBookingRequest) => request<C.CreateBookingResponse>("POST", "/public/bookings", body),
  managed: (token: string) => request<C.ManagedBookingResponse>("GET", `/public/manage/${encodeURIComponent(token)}`),
  cancel: (token: string) => request<{ appointment: C.PublicAppointment }>("POST", `/public/manage/${encodeURIComponent(token)}/cancel`, {}),
  reschedule: (token: string, start: string, stylistId?: string) =>
    request<{ appointment: C.PublicAppointment }>("POST", `/public/manage/${encodeURIComponent(token)}/reschedule`, { start, stylistId }),
};

export const staffApi = {
  me: () => request<C.StaffMeResponse>("GET", "/staff/me", undefined, true),
  appointments: (p: { from: string; to: string; stylistId?: string }) =>
    request<C.StaffAppointmentsResponse>("GET", `/staff/appointments?${qs(p)}`, undefined, true),
  availability: (p: { serviceId: string; stylistId: string; from: string; days?: number; exclude?: string }) =>
    request<C.AvailabilityResponse>("GET", `/staff/availability?${qs(p)}`, undefined, true),
  create: (body: C.StaffCreateAppointmentRequest) =>
    request<{ appointment: C.Appointment }>("POST", "/staff/appointments", body, true),
  update: (id: string, body: C.StaffUpdateAppointmentRequest) =>
    request<{ appointment: C.Appointment }>("PATCH", `/staff/appointments/${encodeURIComponent(id)}`, body, true),
  customer: (phone: string) => request<C.CustomerHistoryResponse>("GET", `/staff/customers/${encodeURIComponent(phone)}`, undefined, true),
  addTimeOff: (body: { stylistId: string; start: string; end: string; reason?: string }) =>
    request<{ timeOff: C.TimeOff }>("POST", "/staff/timeoff", body, true),
  removeTimeOff: (stylistId: string, id: string) =>
    request<void>("DELETE", `/staff/timeoff/${encodeURIComponent(stylistId)}/${encodeURIComponent(id)}`, undefined, true),
  saveSalon: (body: Omit<C.Salon, "timezone" | "established" | "slotStepMinutes">) =>
    request<{ salon: C.Salon }>("PUT", "/staff/salon", body, true),
  saveService: (s: C.Service) => {
    const { id, ...body } = s;
    return request<{ service: C.Service }>("PUT", `/staff/services/${encodeURIComponent(id)}`, body, true);
  },
  saveStylist: (s: C.Stylist) => {
    const { id, ...body } = s;
    return request<{ stylist: C.Stylist }>("PUT", `/staff/stylists/${encodeURIComponent(id)}`, body, true);
  },
  invite: (stylistId: string, email: string, owner: boolean) =>
    request<{ invited: string }>("POST", `/staff/stylists/${encodeURIComponent(stylistId)}/invite`, { email, owner }, true),
  outbox: () => request<{ messages: C.OutboxMessage[] }>("GET", "/staff/outbox", undefined, true),
};
